import {
  Component,
  ViewEncapsulation,
  OnDestroy,
  ViewChild,
  AfterViewInit,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseListCtl, ServiceLocatorService } from '@ncs/ncs-lib';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatSnackBar } from '@angular/material/snack-bar'; // ← ADDED

export interface AppRequestItem {
  id: number;
  applicationName: string;
  organizationName: string;
  userFirstname: string;
  accessKey: string;
  status: string;
  createdDatetime: string;
  qrCode: string | number; // Updated to accept both string and number
}

interface TableColumn {
  key: string;
  label: string;
  sortable: boolean;
  translationKey: string;
}

@Component({
  selector: 'app-app-status',
  templateUrl: './app-status.component.html',
  styleUrls: ['./app-status.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class AppStatusComponent
  extends BaseListCtl
  implements OnDestroy, AfterViewInit
{
  // Define all columns with their properties
  allColumns: TableColumn[] = [
    {
      key: 'sno',
      label: 'APP_REQUEST.TABLE.SNO',
      sortable: false,
      translationKey: 'appRequest.table.headers.sno',
    },
    {
      key: 'applicationName',
      label: 'APP_REQUEST.TABLE.APP',
      sortable: true,
      translationKey: 'appRequest.table.headers.app',
    },
    {
      key: 'subscribedBy',
      label: 'APP_REQUEST.TABLE.SUBSCRIBED_BY',
      sortable: true,
      translationKey: 'appRequest.table.headers.subscribedBy',
    },
    {
      key: 'accessKey',
      label: 'APP_REQUEST.TABLE.ACCESS_KEY',
      sortable: true,
      translationKey: 'appRequest.table.headers.accessKey',
    },
    {
      key: 'status',
      label: 'APP_REQUEST.TABLE.STATUS',
      sortable: true,
      translationKey: 'appRequest.table.headers.status',
    },
    {
      key: 'createdBy',
      label: 'APP_REQUEST.TABLE.DATE',
      sortable: true,
      translationKey: 'appRequest.table.headers.date',
    },
    {
      key: 'qrCode',
      label: 'APP_REQUEST.TABLE.QR_CODE',
      sortable: false,
      translationKey: 'appRequest.table.headers.qrCode',
    },
    {
      key: 'action',
      label: 'APP_REQUEST.TABLE.ACTION',
      sortable: false,
      translationKey: 'appRequest.table.headers.action',
    },
  ];

  // Default column order
  defaultColumnOrder: string[] = this.allColumns.map((col) => col.key);

  // Store current column order
  displayedColumns: string[] = [...this.defaultColumnOrder];

  // Holds table rows
  dataSource: AppRequestItem[] = [];

  @ViewChild('input') input!: ElementRef;

  // SORT STATE
  currentSortColumn = '';
  currentSortDirection: 'asc' | 'desc' | '' = '';

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private snackBar: MatSnackBar // ← ADDED
  ) {
    super('', locator, route);

    // Initialize API endpoint
    this.locator.config
      .getProperty('endpoint.userapps')
      .then((property: string) => {
        this.initApiEndpoint(property);
      });

    let _self = this;
    this.locator.getPathVariable(route, function (params: any) {
      //_self.form.searchParams["appId"] = params["appId"];
    });
  }

  override ngOnInit() {
    // Restore saved column order from localStorage
    this.restoreColumnOrderFromStorage();
    this.displayRequest();
  }

  override search() {
    this.displayRequest();
  }
  ngAfterViewInit() {
    // No Material table initialization needed
  }

  // Get current columns in correct order
  getCurrentColumns(): TableColumn[] {
    return this.displayedColumns
      .map((key) => this.allColumns.find((col) => col.key === key))
      .filter((col) => col) as TableColumn[];
  }

  // Restore column order from localStorage
  private restoreColumnOrderFromStorage() {
    const saved = localStorage.getItem('appStatusColumnOrder');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Validate that all required columns are present
        const requiredColumns = this.allColumns.map((col) => col.key);
        const isValid =
          requiredColumns.every((col) => parsed.includes(col)) &&
          parsed.length === requiredColumns.length;

        if (isValid) {
          this.displayedColumns = parsed;
        }
      } catch (e) {
        console.warn('Failed to parse saved column order:', e);
        this.resetToDefaultOrder();
      }
    }
  }

  // Reset to default column order
  private resetToDefaultOrder() {
    this.displayedColumns = this.allColumns.map((col) => col.key);
    localStorage.removeItem('appStatusColumnOrder');
    this.cdRef.detectChanges();
  }

  // Column drag and drop handler
  drop(event: CdkDragDrop<string[]>) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(
      this.displayedColumns,
      event.previousIndex,
      event.currentIndex
    );

    // Save user's choice
    localStorage.setItem(
      'appStatusColumnOrder',
      JSON.stringify(this.displayedColumns)
    );

    this.cdRef.detectChanges();
  }

  // Check if column order has been changed
  isColumnOrderChanged(): boolean {
    return (
      JSON.stringify(this.displayedColumns) !==
      JSON.stringify(this.defaultColumnOrder)
    );
  }

  // Public restore method for button click
  restoreColumnOrder() {
    if (
      confirm(
        'Are you sure you want to reset all columns to their default positions?'
      )
    ) {
      this.resetToDefaultOrder();
      this.showSnackbar('Column order restored successfully!', 'success'); // ← ADDED
    }
  }

  // ← ADD SNACKBAR METHOD HERE
  private showSnackbar(
    message: string,
    type: 'success' | 'error' | 'info' = 'info'
  ) {
    const panelClass =
      type === 'success'
        ? 'success-snackbar'
        : type === 'error'
        ? 'error-snackbar'
        : 'info-snackbar';

    this.snackBar.open(message, 'Close', {
      duration: 4000,
      verticalPosition: 'top',
      // horizontalPosition: 'right',
      panelClass: [panelClass],
    });
  }

  /**
   * Display data from API
   */
  // display() {
  //   let _self = this;
  //   let url = this.locator.getEndpoint('endpoint.userapps') + '/status';

  //   this.locator.http.get(url, function (res: any, error: boolean) {
  //     if (error) {
  //       // Show snackbar instead of alert ← UPDATED
  //       _self.showSnackbar('Server Error:' + error, 'error');
  //       return;
  //     }

  //     // Update form data
  //     _self.form.list = res.result.data || [];
  //     _self.dataSource = [..._self.form.list];

  //     // Check empty list
  //     if (_self.form.list.length === 0) {
  //       _self.form.message = 'No app requests found';
  //       _self.form.error = true;
  //       // Show snackbar for empty results ← ADDED
  //       _self.showSnackbar('No app requests found', 'info');
  //     } else {
  //       _self.form.message = '';
  //       _self.form.error = false;
  //     }

  //     // Update form status
  //     _self.form.error = !res.success;
  //     _self.form.message = res.message || _self.form.message;

  //     // Show snackbar for API messages ← ADDED
  //     if (res.message && res.message.trim() !== '') {
  //       _self.showSnackbar(res.message, res.success ? 'success' : 'error');
  //     }

  //     _self.cdRef.detectChanges();
  //   });
  // }

  displayRequest() {
    let _self = this;
    let url =
      this.locator.getEndpoint('endpoint.userapps') +
      '/status/' +
      _self.form.pageNo;

    _self.form.searchParams['pageSize'] = _self.form.pageSize;

    this.locator.http.post(
      url,
      _self.form.searchParams,
      function (res: any, error: boolean) {
        if (error) {
          // Show snackbar instead of alert ← UPDATED
          _self.showSnackbar('Server Error:' + error, 'error');
          return;
        }

        _self.form.list = res.result.data || [];

        _self.form.pageCount = res.result.pageCount;
        _self.form.pageSize = res.result.pageSize;
        _self.form.pageNoList = [];

        // Build page number list (original functionality)
        for (let i = 0; i < _self.form.pageCount; i++) {
          _self.form.pageNoList.push(i);
        }

        // Check empty list
        if (_self.form.list.length === 0) {
          _self.form.message = 'No app requests found';
          _self.form.error = true;
          // Show snackbar for empty results ← ADDED
          _self.showSnackbar('No app requests found', 'info');
        } else {
          _self.form.message = '';
          _self.form.error = false;
        }

        // Update form status
        _self.form.error = !res.success;
        _self.form.message = res.message || _self.form.message;

        // Show snackbar for API messages ← ADDED
        if (res.message && res.message.trim() !== '') {
          _self.showSnackbar(res.message, res.success ? 'success' : 'error');
        }

        _self.cdRef.detectChanges();
      }
    );
  }

  /**
   * Calculate serial number
   */
  getSerialNumber(index: number): number {
    return index + 1;
  }

  /**
   * Sorting logic
   */
  onHeaderClick(columnKey: string, event: MouseEvent) {
    // Prevent sorting when clicking on drag handle
    if ((event.target as HTMLElement).closest('.drag-handle')) {
      return;
    }

    const config = this.allColumns.find((c) => c.key === columnKey);
    if (!config?.sortable) return;

    if (this.currentSortColumn === columnKey) {
      // Toggle direction
      this.currentSortDirection =
        this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = columnKey;
      this.currentSortDirection = 'asc';
    }

    this.sortData();
  }

  sortData() {
    const col = this.currentSortColumn;
    const dir = this.currentSortDirection;

    if (!col || !dir) return;

    this.dataSource.sort((a: any, b: any) => {
      let valueA = this.getSortValue(a, col);
      let valueB = this.getSortValue(b, col);

      if (valueA < valueB) return dir === 'asc' ? -1 : 1;
      if (valueA > valueB) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /** Extract correct sorting value */
  getSortValue(item: AppRequestItem, property: string) {
    switch (property) {
      case 'app':
        return item.applicationName?.toLowerCase() || '';
      case 'subscribedBy':
        return (
          (item.userFirstname + ' ' + item.organizationName)?.toLowerCase() ||
          ''
        );
      case 'accessKey':
        return item.accessKey?.toLowerCase() || '';
      case 'status':
        return item.status?.toLowerCase() || '';
      case 'date':
        return item.createdDatetime
          ? new Date(item.createdDatetime).getTime()
          : 0;
      default:
        return '';
    }
  }

  /**
   * Get status badge class based on status
   */
  getStatusClass(status: string): string {
    if (!status) return 'status-default';

    switch (status.toLowerCase()) {
      case 'approved':
        return 'status-approved';
      case 'pending':
        return 'status-pending';
      case 'hold':
        return 'status-hold';
      case 'rejected':
        return 'status-rejected';
      case 'blocked':
        return 'status-blocked';
      case 'no status':
        return 'status-default';
      default:
        return 'status-default';
    }
  }

  // Check if date is overdue
  isOverdue(date: Date | undefined): boolean {
    if (!date) return false;
    return new Date(date) < new Date();
  }

  /**
   * Apply filter to data source
   */
  applyFilter(event: Event) {
    const search = (event.target as HTMLInputElement).value.toLowerCase();

    if (!this.form.list) return;

    if (!search) {
      // Reset to original data
      this.dataSource = [...this.form.list];
      return;
    }

    this.dataSource = this.form.list.filter(
      (data: AppRequestItem) =>
        data.applicationName?.toLowerCase().includes(search) ||
        data.organizationName?.toLowerCase().includes(search) ||
        data.userFirstname?.toLowerCase().includes(search) ||
        data.accessKey?.toLowerCase().includes(search) ||
        data.status?.toLowerCase().includes(search)
    );

    // Show snackbar for search results ← ADDED
    if (search.trim() !== '' && this.dataSource.length === 0) {
      this.showSnackbar('No matching records found', 'info');
    }
  }

  /**
   * Clear search filter
   */
  clearSearch() {
    if (this.input?.nativeElement) {
      this.input.nativeElement.value = '';
    }
    this.dataSource = [...this.form.list];
  }

  /**
   * Change app status
   */
  changeStatus(id: number, status: string) {
    let _self = this;
    let url =
      this.locator.getEndpoint('endpoint.userapps') +
      '/changestatus/' +
      id +
      '/' +
      status;

    this.locator.http.get(url, function (res: any, error: boolean) {
      if (error) {
        // Show snackbar instead of alert ← UPDATED
        _self.showSnackbar('Server Error:' + error, 'error');
        return;
      }

      _self.form.error = !res.success;
      _self.form.message = res.message;

      if (res.success) {
        // Show success message in snackbar ← UPDATED
        _self.showSnackbar(
          `Status changed to ${status} successfully!`,
          'success'
        );
        _self.form.message = `Status changed to ${status} successfully!`;
        _self.form.error = false;

        // Refresh the data
        _self.displayRequest();
      } else {
        // Show error in snackbar ← UPDATED
        _self.showSnackbar('Error: ' + res.message, 'error');
      }
    });
  }

  /**
   * Delete Status
   */
  deleteStatus(id: number) {
    let _self = this;

    // Use Material Dialog or confirm
    if (confirm('Are you sure you want to delete this app request?')) {
      let url = this.locator.getEndpoint('endpoint.userapps') + '/delete/' + id;

      this.locator.http.get(url, function (res: any, error: any) {
        if (error) {
          // Show server error message
          _self.showSnackbar('Server Error:' + error, 'error');
          return;
        }

        _self.form.error = !res.success;
        _self.form.message = res.message || res.result?.message;

        // Show message from server response
        const message = res.message || res.result?.message;
        if (message && message.trim() !== '') {
          _self.showSnackbar(message, res.success ? 'success' : 'error');
        } else if (res.success) {
          // Fallback if server returns success but no message
          _self.showSnackbar('Operation completed successfully', 'success');
        } else {
          // Fallback if server returns error but no message
          _self.showSnackbar('Operation failed', 'error');
        }

        if (res.success) {
          // Refresh the data
          _self.displayRequest();
        }
      });
    }
  }

  /**
   * Get document URL - Updated to handle string QR codes
   */
  override getDoc(qrCodeId: string | number): string {
    if (
      !qrCodeId ||
      qrCodeId === null ||
      qrCodeId === undefined ||
      qrCodeId === ''
    ) {
      return '#';
    }

    // Convert to string and check if it's numeric
    const qrCodeStr = String(qrCodeId);
    const isNumeric = /^\d+$/.test(qrCodeStr);

    if (!isNumeric) {
      console.warn('QR Code ID is not numeric:', qrCodeId);
      return '#';
    }

    const timestamp = new Date().getTime();
    return `https://dev.nenosystems.in/API/DOCS/api/doc/${qrCodeStr}?t=${timestamp}`;
  }

  /**
   * Get document thumbnail URL - Updated to handle string QR codes
   */
  override getDocThumbnail(qrCodeId: string | number): string {
    if (
      !qrCodeId ||
      qrCodeId === null ||
      qrCodeId === undefined ||
      qrCodeId === ''
    ) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNSAxNUgzNVYzNUgxNVYxNVoiIGZpbGw9IiNEOEUxRTYiLz4KPHBhdGggZD0iTTIwIDIwaDJ2MTBoLTJ6TTI4IDIwaDJ2MTBoLTJ6TTIwIDI4aDEwdjJIMjB6IiBmaWxsPSIjOEU5N0FGIi8+Cjwvc3ZnPgo=';
    }

    return this.getDoc(qrCodeId);
  }

  /**
   * Check if QR code is available - Updated to handle string QR codes
   */
  hasQRCode(qrCodeId: string | number): boolean {
    if (
      !qrCodeId ||
      qrCodeId === null ||
      qrCodeId === undefined ||
      qrCodeId === ''
    ) {
      return false;
    }

    // Convert to string and check if it's numeric
    const qrCodeStr = String(qrCodeId);
    return /^\d+$/.test(qrCodeStr);
  }

  /**
   * Handle image loading errors
   */
  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src =
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNSAxNUgzNVYzNUgxNVYxNVoiIGZpbGw9IiNEOEUxRTYiLz4KPHBhdGggZD0iTTIwIDIwaDJ2MTBoLTJ6TTI4IDIwaDJ2MTBoLTJ6TTIwIDI4aDEwdjJIMjB6IiBmaWxsPSIjOEU5N0FGIi8+Cjwvc3ZnPgo=';
    imgElement.alt = 'QR Code not available';
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  isExpanded = false;

  toggleSearch(): void {
    this.isExpanded = !this.isExpanded;
  }

  sortedField: string | null = null;
  sortOrder: 'ASC' | 'DESC' | null = null;
  sortTable(field: string): void {
    let _self = this;
    if (this.sortedField === field) {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortedField = field;
      this.sortOrder = 'ASC';
    }
    _self.form.searchParams['orderBy'] = this.sortedField;
    _self.form.searchParams['asc'] = this.sortOrder;
    _self.displayRequest();
  }

  //---------------------------search method for apllication ------------------------>

  public AppList: any[] = [];

  applicationName: string = '';
  userFirstname: string = '';
  accessKey: string = '';
  status: string = '';
  createdDatetime: string = '';

  requestSearch() {
    console.log('Hello world : ');

    const url =
      this.locator.getEndpoint('endpoint.userapps') + '/appRequestUser';

    if (this.applicationName?.trim()) {
      this.form.searchParams['applicationName'] = this.applicationName.trim();
    }

    if (this.userFirstname?.trim()) {
      this.form.searchParams['userFirstname'] = this.userFirstname.trim();
    }

    if (this.accessKey?.toString().trim()) {
      this.form.searchParams['accessKey'] = this.accessKey.toString().trim();
    }

    if (this.createdDatetime?.toString().trim()) {
      this.form.searchParams['createdDatetime'] = this.createdDatetime
        .toString()
        .trim();
    }

    if (this.status?.toString().trim()) {
      this.form.searchParams['status'] = this.status
        .toString()
        .trim();
    }

    console.log('Search Payload : ', this.form.searchParams);

    this.locator.http.post(
      url,
      this.form.searchParams,
      (res: any, error: boolean) => {
        if (error) {
          alert('Server Error: ' + error);
          this.AppList = [];
          return;
        }

        this.AppList = res.result?.data || [];
        this.form.list = this.AppList;
        console.log('Filtered User List:', this.AppList);
      }
    );
  }

  resetFilters() {
    this.applicationName = '';
    this.userFirstname = '';
    this.accessKey = '';
    this.createdDatetime = '';
    this.status = '';

    // 🔹 Clear backend search params
    this.form.searchParams = {};
    this.form.pageNo = 0;

    this.displayRequest();
  }
}
